import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, DollarSign, Mail, Loader2, ShoppingCart, Send } from "lucide-react";

export default function GhostRecovery() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});
  const [error, setError] = useState("");

  const analyze = async () => {
    setLoading(true); setError("");
    try {
      const { data: result } = await API.get("/tools/ghost-recovery/analyze");
      setData(result);
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const sendRecovery = async (checkoutId) => {
    setSending(p => ({ ...p, [checkoutId]: true }));
    try {
      const { data: result } = await API.post(`/tools/ghost-recovery/send-recovery/${checkoutId}`);
      if (result.success) toast.success(`Recovery email sent to ${result.email}`);
      else toast.error(result.error || "Send failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSending(p => ({ ...p, [checkoutId]: false }));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="ghost-recovery-page">
      <nav className="border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">NRD<span className="text-[#00FF41]">.</span></Link>
            <span className="text-xs font-mono text-[#00FF41]">/ GHOST-RECOVERY</span>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#00FF41] flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-1">Ghost-Recovery v2</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Abandoned Cart Recovery</h1>
          </div>
          <button onClick={analyze} disabled={loading} className="flex items-center gap-2 bg-[#00FF41] text-[#0A0A0A] px-6 py-3 text-sm font-bold hover:bg-[#00CC33] disabled:opacity-50" data-testid="analyze-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {loading ? "Scanning..." : "Scan Abandoned Carts"}
          </button>
        </div>
        {error && <div className="p-4 bg-red-900/30 border border-red-800 text-sm text-red-400 mb-6">{error}</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-0 border border-[#1E1E1E] bg-[#111111] mb-8">
              {[
                { label: "Abandoned", val: data.stats.total_abandoned, color: "text-red-400" },
                { label: "Recovered", val: data.stats.total_recovered, color: "text-[#00FF41]" },
                { label: "Lost Revenue", val: `$${data.stats.total_lost_revenue.toLocaleString()}`, color: "text-red-400" },
                { label: "Recoverable", val: data.stats.recoverable_count, color: "text-yellow-400" },
                { label: "Avg Cart", val: `$${data.stats.avg_cart_value}` },
                { label: "Recovery Rate", val: `${data.stats.recovery_rate}%`, color: "text-[#00FF41]" },
              ].map((s, i) => (
                <div key={i} className={`p-5 ${i < 5 ? 'border-r border-[#1E1E1E]' : ''}`}>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">{s.label}</p>
                  <p className={`font-heading text-2xl font-black ${s.color || ''}`}>{s.val}</p>
                </div>
              ))}
            </div>
            <div className="border border-[#1E1E1E] bg-[#111111]">
              <div className="border-b border-[#1E1E1E] px-5 py-3 bg-[#0A0A0A]">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Abandoned Checkouts</p>
              </div>
              {data.checkouts.filter(c => !c.is_recovered).map((c, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-[#1E1E1E] last:border-0" data-testid={`checkout-row-${i}`}>
                  <ShoppingCart size={16} className="text-red-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{c.customer_name}</p>
                    <p className="text-xs text-slate-500">{c.email} &middot; {c.item_count} items</p>
                    <p className="text-xs text-slate-600 mt-0.5">{c.items.map(it => it.title).join(", ")}</p>
                  </div>
                  <p className="text-lg font-bold text-[#00FF41] font-mono">${c.total_price}</p>
                  {c.has_email && (
                    <button onClick={() => sendRecovery(c.id)} disabled={sending[c.id]}
                      className="flex items-center gap-1.5 bg-[#00FF41] text-[#0A0A0A] px-3 py-2 text-xs font-bold hover:bg-[#00CC33] disabled:opacity-50"
                      data-testid={`send-recovery-${c.id}`}>
                      {sending[c.id] ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Recover
                    </button>
                  )}
                </div>
              ))}
              {data.checkouts.filter(c => !c.is_recovered).length === 0 && (
                <p className="text-center py-8 text-slate-500 text-sm">No abandoned checkouts found</p>
              )}
            </div>
          </>
        )}
        {!data && !loading && <div className="text-center py-20 text-slate-500"><ShoppingCart size={48} className="mx-auto mb-4 opacity-30" /><p>Click "Scan Abandoned Carts" to find lost revenue</p></div>}
      </div>
    </div>
  );
}
